/**
 * BLRRD Editor — Long-press → reset for sliders on coarse pointers.
 *
 * Touch equivalent of the existing dblclick-to-reset on slider rows. Only
 * fires when the user is on a coarse pointer (touch device); mouse stays
 * on dblclick to keep desktop behaviour unchanged.
 *
 * The reset action is delegated — we dispatch the same input+change events
 * the legacy dblclick handler dispatches, so the existing reset path is
 * the single source of truth.
 */

const LONG_PRESS_MS = 550;
const MOVE_TOLERANCE_PX = 10;

export function isCoarsePointer() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
}

/**
 * Bind long-press reset on a row's label + value display. We deliberately
 * don't bind onto the slider thumb itself — that would conflict with the
 * normal drag-to-adjust gesture.
 *
 * @param {HTMLElement} row   .ed-slider-row
 * @param {Function} reset    Zero-arg function that resets the slider value.
 *                            (Caller already knows the default.)
 */
export function bindLongPressReset(row, reset) {
    if (!row || typeof reset !== 'function') return () => {};
    const label = row.querySelector('.ed-slider-label');
    const valEl = row.querySelector('.ed-slider-val');
    const targets = [label, valEl].filter(Boolean);
    if (targets.length === 0) return () => {};

    let timer = null;
    let startX = 0, startY = 0;
    let armedPointerId = null;

    function clearTimer() {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        armedPointerId = null;
    }

    function onDown(e) {
        if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
        if (!isCoarsePointer()) return;
        startX = e.clientX;
        startY = e.clientY;
        armedPointerId = e.pointerId;
        timer = setTimeout(() => {
            timer = null;
            armedPointerId = null;
            reset();
        }, LONG_PRESS_MS);
    }

    function onMove(e) {
        if (e.pointerId !== armedPointerId) return;
        if (Math.hypot(e.clientX - startX, e.clientY - startY) > MOVE_TOLERANCE_PX) {
            clearTimer();
        }
    }

    function onUp() { clearTimer(); }

    targets.forEach((t) => {
        t.addEventListener('pointerdown',   onDown);
        t.addEventListener('pointermove',   onMove);
        t.addEventListener('pointerup',     onUp);
        t.addEventListener('pointercancel', onUp);
        t.addEventListener('pointerleave',  onUp);
    });

    return function detach() {
        clearTimer();
        targets.forEach((t) => {
            t.removeEventListener('pointerdown',   onDown);
            t.removeEventListener('pointermove',   onMove);
            t.removeEventListener('pointerup',     onUp);
            t.removeEventListener('pointercancel', onUp);
            t.removeEventListener('pointerleave',  onUp);
        });
    };
}

/**
 * Walk all slider rows currently in the DOM and bind long-press reset.
 * Idempotent — repeated calls just re-bind (cheap; same handlers).
 */
export function bindAllSliderLongPress() {
    if (!isCoarsePointer()) return () => {};
    const detachers = [];
    document.querySelectorAll('.ed-slider-row').forEach((row) => {
        const sl = row.querySelector('.ed-slider-input');
        if (!sl) return;
        const reset = () => {
            /* Match the legacy dblclick path exactly: dispatch input + change
             * after writing the slider's "resetTo" attribute (preferred) or
             * its `data-default` if present, falling back to 0. */
            const isCurveInt = sl.id && sl.id.startsWith('slCurve');
            const dataDef = sl.getAttribute('data-default');
            const def = dataDef !== null ? parseFloat(dataDef) : (isCurveInt ? 100 : 0);
            sl.value = String(def);
            sl.dispatchEvent(new Event('input', { bubbles: true }));
            sl.dispatchEvent(new Event('change', { bubbles: true }));
        };
        detachers.push(bindLongPressReset(row, reset));
    });
    return function detachAll() { detachers.forEach((fn) => { try { fn(); } catch {} }); };
}
